import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { useCardDraft } from "../common/useCardDraft";
import {
  durationToSinceWhen,
  formatShortDate,
  sinceWhenToDuration,
  toIsoUtc,
} from "../chiefComplaints/dateHelpers";
import { Stepper } from "../controls";
import {
  ChevronUpIcon,
  ChipPlusIcon,
} from "../labInvestigations/labIcons";
import AllergyService from "./AllergyService";
import AllergenCatalogAutocomplete from "./AllergenCatalogAutocomplete";
import AllergenCatalogService from "./AllergenCatalogService";
import {
  upsertReaction,
  removeReaction,
} from "./data";
import { ALG_V2_REFRESH } from "./events";
import { listReactions } from "./ReactionsCatalogService";
import {
  fetchCategories as fetchAllergyCategories,
  normaliseCategory,
} from "./AllergyCategoriesService";

/**
 * ONE allergy sheet — add or update — owning all of its own field state.
 *
 * This used to be the body of AllergyEditor, with every field hoisted into
 * that component and gated on a single `formOpen` + `editing` pair. It was
 * split out so the rail can hold a STACK of sheets: each card needs its own
 * allergen / reactions / dates / description, and sibling cards must not see
 * each other's values. AllergyEditor now only decides which cards exist and
 * in what order; everything below the card border lives here.
 *
 * Two modes, unchanged from before the split:
 *   add    "Card · Add allergy (in-screen)" (3071:3242, 340 wide) — allergen
 *          (federated fuzzy search, a REAL catalog pick required) · reactions
 *          (token box + suggested chip rail) · First noted (required) / Last
 *          reaction · description.
 *   update "Editor · A4 filled (Allergy)" (3296:2854) — the allergen shows
 *          ONLY in the "Update · <name>" heading: no locked field, no Category
 *          field. name/catalogId/mCode/source are omitted from the wire (the
 *          server preserves identity) and the STORED category rides through
 *          unchanged on the body.
 *
 * Rules the dialog carried and this sheet keeps:
 *   • a legacy description already over the 250-word label — written before
 *     this UI, or via bridge, which never enforced it — edits UNCAPPED, so the
 *     cap can never trim existing content one word per keystroke. The limit is
 *     decided ONCE, from the record this sheet mounted on.
 *   • every reaction carries its source ("controlled" | "free-text") on the
 *     wire, in both modes.
 *   • First noted is required on the board (red asterisk) — enforced here.
 *   • Category is intentionally NOT a field: on add it auto-fills from the
 *     catalog pick (free text ⇒ "Others"); on update it is never re-asked.
 *   • expectedVersion is re-read (svc.list) immediately before every mutation,
 *     never cached. Sibling sheets are blocked from saving while ours is in
 *     flight, so that read cannot go stale between the read and the write.
 */

const MAX_DESC_WORDS = 250;

const countWords = (text) => (text || "").split(/\s+/).filter(Boolean).length;

/** Cap at `limit` words (Infinity = no cap — see the legacy-description note
 *  in the header). */
const capWords = (text, limit) => {
  if (!isFinite(limit)) return text;
  const words = text.split(/(\s+)/);
  let count = 0;
  let out = "";
  for (const w of words) {
    if (!/\s/.test(w) && w.length > 0) {
      count += 1;
      if (count > limit) break;
    }
    out += w;
  }
  return count > limit ? out : text;
};

// Normalise reactions coming from an AI captured payload (comma-string,
// list-of-strings, or list-of-objects) into the {name} shape AllergyForm
// holds internally. Per-reaction `source` was retired in 2026-09 and is
// stripped here so it never leaks back to the wire body on save.
const seedReactionsFromPayload = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((r) => (r && typeof r === "object" ? { name: r.name } : { name: String(r || "") }))
      .filter((r) => r.name && r.name.trim())
      .map((r) => ({ name: r.name.trim() }));
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
  }
  return [];
};

// Category normalisation lives in AllergyCategoriesService.js — the
// authoritative enum is served by api-allergy-v2-service's
// GET /api/v1/allergen-config, cached at module scope, seeded with a
// FALLBACK list so this component renders correctly on the very first
// mount. `normaliseCategory` handles legacy shims (Drug→Salt,
// Liquid→Liquids, Polutry→Poultry, Others→Other) so already-saved
// rows + old AI outputs still map to a canonical value.

const AllergyForm = ({
  formId,
  patientId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload,
  onSave,
  onReject,
  saving,
  error,
  themeClass,
  lockName,
  isReopen,
}) => {
  const isUpdate = !!editing;
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  const lockAllergenName = !!lockName;

  // Allergen identity — snapshot from the catalog pick (or free text).
  // Add-only: update never re-asks for identity (see the header), so these
  // stay blank there and `editing` alone drives the heading and the stored
  // category on the wire.
  //
  // Transcript-mode seeding: AI's `add_allergy` puts the allergen in
  // `substance` (payload alias). Category defaults to "Others" if the AI
  // didn't classify. We treat an AI-seeded substance as a synthetic
  // free-text pick so the reviewer can still save — the transcript flow
  // has no catalog wired up on the AI side.
  // ZY-10356 Phase 1 — transcript-mode draft preservation. No-op in
  // standalone.
  const [draft, saveDraft] = useCardDraft(formId);

  const [name, setName] = useState(() => (
    draft && "name" in draft ? draft.name : ((p && (p.substance || p.name || p.value)) || "")
  ));
  const [collapsed, setCollapsed] = useState(false);
  const [category, setCategory] = useState(() => (
    draft && "category" in draft
      ? draft.category
      : (p && p.category ? normaliseCategory(p.category) : "Others")
  ));
  const [catalogId, setCatalogId] = useState(() => (
    draft && "catalogId" in draft ? draft.catalogId : ""
  ));
  const [mCode, setMCode] = useState(() => (
    draft && "mCode" in draft ? draft.mCode : ""
  ));
  // Add-in-transcript flow: no catalog id, so mark it "free-text" (the
  // enum the backend accepts alongside `salt`/`catalog`). Update mode
  // keeps `free-text` as its idle default.
  const [source, setSource] = useState("free-text");

  // Seeded in the useState INITIALISER, not from an effect: a sheet mounts
  // once per record and never switches the record under itself. Legacy
  // records may still carry `source` on each reaction — normalise to
  // `{name}` so a re-save doesn't roundtrip the retired field.
  const [reactions, setReactions] = useState(() => {
    if (draft && "reactions" in draft) return draft.reactions;
    if (isUpdate) {
      return (editing.reactions || [])
        .filter((r) => r && r.name && r.name.trim())
        .map((r) => ({ name: r.name.trim() }));
    }
    if (p) return seedReactionsFromPayload(p.reactions);
    return [];
  });
  const [reactionText, setReactionText] = useState("");

  // Reactions catalog — fetched once from api-allergy-v2-service via the
  // module-level cache in ReactionsCatalogService. Populates BOTH the
  // "Suggested" chip rail (isCommon=true) and the searchable dropdown.
  // Free-text is no longer accepted (product decision, 2026-09).
  const [reactionsCatalog, setReactionsCatalog] = useState([]);
  const [reactionsDropdownOpen, setReactionsDropdownOpen] = useState(false);
  // Track whether we've already run the transcript-mode seed filter so
  // the reviewer's later Reaction edits don't get re-filtered (they
  // might click Suggested chips whose names are added async; the filter
  // only cares about the ORIGINAL AI seed).
  const seedFilteredRef = useRef(false);
  useEffect(() => {
    let alive = true;
    listReactions().then((list) => {
      if (!alive) return;
      const catalog = list || [];
      setReactionsCatalog(catalog);
      // Transcript-mode AI seed filter: `seedReactionsFromPayload` on
      // mount accepts any string the LLM emitted ("body pain", "chest
      // discomfort") because it has no catalog to validate against yet.
      // Now the catalog has landed — enforce the catalog-only rule the
      // standalone picker already applies: keep exact matches as chips
      // (snapping to the catalog's canonical casing), move everything
      // else into Description so the reviewer still sees what the AI
      // heard but it doesn't ship as a bogus Reaction chip. Scoped to
      // transcript ADD only — standalone + update seeds come from the
      // DB which the server already validated.
      if (seedFilteredRef.current) return;
      if (isUpdate || !isTranscript) return;
      seedFilteredRef.current = true;
      const byLower = new Map(
        catalog.map((r) => [String(r.reaction || "").trim().toLowerCase(), String(r.reaction || "").trim()]),
      );
      setReactions((prev) => {
        if (!prev || prev.length === 0) return prev;
        const kept = [];
        const dropped = [];
        for (const r of prev) {
          const nm = (r && r.name || "").trim();
          if (!nm) continue;
          const canonical = byLower.get(nm.toLowerCase());
          if (canonical) {
            // De-dup against earlier catalog match in the same list.
            if (!kept.some((k) => k.name.toLowerCase() === canonical.toLowerCase())) {
              kept.push({ name: canonical });
            }
          } else {
            dropped.push(nm);
          }
        }
        if (dropped.length > 0) {
          // Append the dropped free-text names to Description so the
          // reviewer sees what the AI picked up and can rephrase into
          // a catalog match if they want to.
          setDescription((d) => {
            const prefix = (d || "").trim();
            const extra = dropped.join(", ");
            if (!prefix) return extra;
            if (prefix.toLowerCase().includes(extra.toLowerCase())) return prefix;
            return `${prefix}; ${extra}`;
          });
        }
        if (kept.length === prev.length && dropped.length === 0) return prev;
        return kept;
      });
    });
    // Warm the Category enum cache — the module seeded a FALLBACK list
    // synchronously so this component renders immediately; the fetch
    // replaces the cache with whatever the backend serves so any later
    // widening on the server flows through without a code change here.
    fetchAllergyCategories();
    return () => { alive = false; };
  // Mount-only: the filter runs once when the catalog lands. The ref
  // above makes it idempotent against the setReactionsCatalog re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Duration steppers — same UX as CC's Since when. First noted is
  // REQUIRED (mirrors CC's Duration invariant); Last reaction is
  // OPTIONAL. Seeding rules:
  //   • Update mode → reverse-compute Y/M/D from the stored ISO date
  //     via sinceWhenToDuration. Toggle stays off (a real historical
  //     date, not "today").
  //   • Transcript mode → same reverse-compute from p.since_date /
  //     p.last_reaction_date (both anchored on REFERENCE DATE by the
  //     agent). Toggle stays off.
  //   • Fresh Add → nullable steppers (Y/M/D=null → renders as "-"),
  //     toggle off. First noted's canSubmit guard fails until the
  //     reviewer either types a duration or flips the toggle on.
  const seedDurationFromIso = (iso) => {
    if (!iso) return { y: null, m: null, d: null, fromToday: false };
    const dur = sinceWhenToDuration(iso);
    // sinceWhenToDuration returns 0/0/0 for "today" ISO. Reflect
    // that back as fromToday=true so the toggle reads cleanly.
    if (dur.years === 0 && dur.months === 0 && dur.days === 0) {
      return { y: 0, m: 0, d: 0, fromToday: true };
    }
    return { y: dur.years, m: dur.months, d: dur.days, fromToday: false };
  };
  const firstNotedSeed = (() => {
    if (isUpdate) return seedDurationFromIso(editing.firstNoted);
    if (p && (p.since_date || p.firstNoted)) return seedDurationFromIso(p.since_date || p.firstNoted);
    return { y: null, m: null, d: null, fromToday: false };
  })();
  const lastReactionSeed = (() => {
    if (isUpdate) return seedDurationFromIso(editing.lastReaction);
    if (p && (p.last_reaction_date || p.lastReaction)) return seedDurationFromIso(p.last_reaction_date || p.lastReaction);
    return { y: null, m: null, d: null, fromToday: false };
  })();

  const [firstNotedY, setFirstNotedY] = useState(() => (draft && "firstNotedY" in draft ? draft.firstNotedY : firstNotedSeed.y));
  const [firstNotedM, setFirstNotedM] = useState(() => (draft && "firstNotedM" in draft ? draft.firstNotedM : firstNotedSeed.m));
  const [firstNotedD, setFirstNotedD] = useState(() => (draft && "firstNotedD" in draft ? draft.firstNotedD : firstNotedSeed.d));
  const [firstNotedFromToday, setFirstNotedFromToday] = useState(() => (draft && "firstNotedFromToday" in draft ? draft.firstNotedFromToday : firstNotedSeed.fromToday));

  const [lastReactionY, setLastReactionY] = useState(() => (draft && "lastReactionY" in draft ? draft.lastReactionY : lastReactionSeed.y));
  const [lastReactionM, setLastReactionM] = useState(() => (draft && "lastReactionM" in draft ? draft.lastReactionM : lastReactionSeed.m));
  const [lastReactionD, setLastReactionD] = useState(() => (draft && "lastReactionD" in draft ? draft.lastReactionD : lastReactionSeed.d));
  const [lastReactionFromToday, setLastReactionFromToday] = useState(() => (draft && "lastReactionFromToday" in draft ? draft.lastReactionFromToday : lastReactionSeed.fromToday));
  const [description, setDescription] = useState(() => {
    if (draft && "description" in draft) return draft.description;
    if (isUpdate) return editing.description || "";
    if (p) return p.description || "";
    return "";
  });
  const [dupError, setDupError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change.
  useEffect(() => {
    saveDraft({
      name, category, catalogId, mCode, reactions,
      firstNotedY, firstNotedM, firstNotedD, firstNotedFromToday,
      lastReactionY, lastReactionM, lastReactionD, lastReactionFromToday,
      description,
    });
  }, [
    saveDraft,
    name, category, catalogId, mCode, reactions,
    firstNotedY, firstNotedM, firstNotedD, firstNotedFromToday,
    lastReactionY, lastReactionM, lastReactionD, lastReactionFromToday,
    description,
  ]);

  // Set of lowercased names of the patient's currently-recorded allergies,
  // used to grey out already-added rows in the AllergenCatalogAutocomplete
  // dropdown. Populated once on mount; the JIT dup-check on `handleAllergenPick`
  // stays as a belt-and-braces network-fresh guard.
  const [activeAllergenNames, setActiveAllergenNames] = useState(() => new Set());

  const mounted = useRef(true);
  // Search-wrap ref — used by the outside-click listener below to
  // decide whether a mousedown landed inside the picker (input or
  // dropdown menu, both of which live under this wrap) or outside it.
  const searchWrapRef = useRef(null);

  // Close the reactions dropdown on outside mousedown / Escape. Runs
  // ONLY while the dropdown is open so the section doesn't hold a
  // document listener the rest of its life. Same pattern the FH
  // Conditions picker (IcdAutocomplete) uses.
  useEffect(() => {
    if (!reactionsDropdownOpen) return undefined;
    const onDoc = (e) => {
      const wrap = searchWrapRef.current;
      if (wrap && !wrap.contains(e.target)) setReactionsDropdownOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setReactionsDropdownOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [reactionsDropdownOpen]);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!patientId) return;
    const svc = new AllergyService();
    svc.list(patientId, (res) => {
      if (!mounted.current) return;
      const names = new Set();
      for (const a of (res && res.allergies) || []) {
        const key = ((a && a.name) || "").trim().toLowerCase();
        // On an UPDATE sheet exclude the record being edited so its own
        // name doesn't render as "already added" against itself.
        if (isUpdate && editing && editing.allergyId === a.allergyId) continue;
        if (key) names.add(key);
      }
      setActiveAllergenNames(names);
    });
  }, [patientId, isUpdate, editing]);

  const isAllergenAlreadyActive = useCallback(
    (row) => {
      const key = ((row && row.name) || "").trim().toLowerCase();
      return !!key && activeAllergenNames.has(key);
    },
    [activeAllergenNames],
  );

  // Word cap in force for the description this sheet loaded. Switched off
  // (Infinity) for a legacy row that arrived over the limit. Fixed at mount —
  // an add sheet always carries the cap unless the AI seeded an already-
  // over-limit blob (rare, but never trim on keystroke).
  const descLimit = useRef(
    countWords((editing && editing.description) || (p && p.description) || "") > MAX_DESC_WORDS
      ? Infinity
      : MAX_DESC_WORDS
  );

  // The rail needs to know whether this sheet has an allergen on it yet: a
  // second "+ Add allergy" click must not bury an untouched blank sheet under
  // a new one. Update sheets always count as filled — their allergen is fixed
  // and comes from the record.
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, isUpdate || !!name.trim());
    }
  }, [formId, isUpdate, name, onFilledChange]);

  // Transcript-mode allergen auto-pick on exact catalog match. The AI's
  // substance arrives as free-text (source="free-text") because the
  // agent has no catalog binding, so the Add gate blocks save until the
  // reviewer manually picks from the AllergenCatalogAutocomplete. For
  // the common "AI heard the exact catalog spelling" case we query the
  // catalog ONCE on mount and auto-pick the entry only when its name
  // case-insensitively matches the seed verbatim (no fuzzy acceptance,
  // no close-enough). Misses leave the field as-is — reviewer picks
  // from the dropdown as before. Add still requires catalogPicked, so
  // nothing weaker than an exact match can slip through to the wire.
  const allergenAutoPickRef = useRef(false);
  useEffect(() => {
    if (allergenAutoPickRef.current) return;
    if (isUpdate || !isTranscript) return;
    const seedName = (p && (p.substance || p.name || p.value) || "").toString().trim();
    if (!seedName) return;
    if (source && source !== "free-text") return;  // reviewer already picked
    allergenAutoPickRef.current = true;
    const svc = new AllergenCatalogService();
    svc.search(
      seedName,
      (rows) => {
        if (!mounted.current) return;
        const needle = seedName.toLowerCase();
        const exact = (rows || []).find(
          (r) => r && String(r.name || "").trim().toLowerCase() === needle,
        );
        if (!exact) return;
        // Only auto-pick when the user hasn't typed something different
        // in the meantime (StrictMode double-invoke, slow network, etc.).
        if ((name || "").trim().toLowerCase() !== needle) return;
        handleAllergenPick({
          name:      exact.name,
          category:  exact.category,
          catalogId: exact.id || "",
          mCode:     exact.mCode || "",
          source:    exact.source || "catalog",
        });
      },
      () => {},
    );
  // Mount-only; `allergenAutoPickRef` makes this idempotent against
  // state churn from the pick itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAllergenPick = useCallback(
    ({ name: n, category: cat, catalogId: cid, mCode: mc, source: src }) => {
      setName(n);
      setCategory(cat || "Other");
      setCatalogId(cid || "");
      setMCode(mc || "");
      setSource(src || "free-text");
      setDupError("");
      // Upstream rule: the same allergen must not be added twice. The rail
      // has no list prop, so the check reads the doc fresh on each pick —
      // the same JIT pattern the submit path uses. Comparison is on the
      // display name (trimmed, case-insensitive), which is what a clinician
      // actually sees. Only a real catalog pick can clash, since free text
      // can no longer be saved at all.
      //
      // Salt picks have no catalog id (salts are a distinct-name query,
      // not a document lookup), so the dup check runs whenever the
      // source is a REAL pick — salt / catalog — regardless of ids.
      const isCatalogPick = src && src !== "free-text";
      if (!isCatalogPick) return;
      const key = (n || "").trim().toLowerCase();
      if (!key || !patientId) return;
      const svc = new AllergyService();
      svc.list(patientId, (fresh) => {
        if (!mounted.current) return;
        const clash = ((fresh && fresh.allergies) || []).some(
          (a) => (a.name || "").trim().toLowerCase() === key
        );
        if (clash) setDupError(`"${(n || "").trim()}" is already in the allergies list.`);
      });
    },
    [patientId]
  );

  // Free-text is no longer accepted — Enter/blur commit paths are gone,
  // and the search input's value is used only to filter the dropdown.
  // Legacy records saved with a reaction that isn't in the current
  // catalog still render on the chip rail so no historical data
  // disappears from the sheet.
  const commitFromCatalog = (reactionName) => {
    setReactions((prev) => upsertReaction(prev, reactionName));
    setReactionText("");
    setReactionsDropdownOpen(false);
  };

  // Dropdown result filter — case-insensitive substring match on the
  // reaction name. Already-picked reactions render greyed with an
  // "already added" pill (silent no-op on click) so the reviewer sees
  // "why can't I add this?" without a modal.
  const reactionSearchQ = reactionText.trim().toLowerCase();
  const pickedKeys = useMemo(
    () => new Set(reactions.map((r) => r.name.trim().toLowerCase())),
    [reactions]
  );
  const filteredReactions = useMemo(
    () => reactionsCatalog.filter((r) => {
      const nm = String(r.reaction || "").toLowerCase();
      if (!reactionSearchQ) return true;
      return nm.includes(reactionSearchQ);
    }).slice(0, 50),
    [reactionsCatalog, reactionSearchQ]
  );

  // Upstream rule: add requires a REAL catalog selection — typing without
  // picking from the dropdown is not saveable, so every stored allergen
  // ties back to a source the backend recognises: `salt` (from api-
  // medicine-service /medicine/salts) or `catalog` (own foods/other).
  // Salt picks carry no id/mCode (they're a distinct-name query, not a
  // document lookup), so `source` is the canonical signal.
  //
  // Transcript-mode add: the AI has no catalog binding, so we relax the
  // rule — the review pass is the human gate. The wire body still ships
  // `source="free-text"`, which the backend accepts alongside catalog
  // picks.
  const catalogPicked = source && source !== "free-text";
  const busy = submitting || !!saving;

  // At least one of Description or Reaction must be present on both Add
  // and Update — an allergy record with neither says nothing clinically
  // useful. Reactions is an array of {name, source}; a trimmed
  // description with any content clears the gate.
  const hasReactionOrDescription =
    reactions.length > 0 || description.trim().length > 0;

  // Auto-flip First noted's toggle when the reviewer manually lands on
  // 0/0/0 (mirrors CC's Duration rule — all-zero is semantically
  // "today", so the toggle IS the way we record that intent). Last
  // reaction stays user-controlled: OPTIONAL fields should honour an
  // explicit "no value" state, so we don't auto-fill.
  useEffect(() => {
    if (firstNotedY === 0 && firstNotedM === 0 && firstNotedD === 0 && !firstNotedFromToday) {
      setFirstNotedFromToday(true);
    }
  }, [firstNotedY, firstNotedM, firstNotedD, firstNotedFromToday]);

  // Derived dates. Same rule as CC: fromToday → new Date(); else any
  // non-zero → today minus duration; else null (empty).
  const firstNotedHasAny = (firstNotedY || 0) > 0 || (firstNotedM || 0) > 0 || (firstNotedD || 0) > 0;
  const derivedFirstNoted = firstNotedFromToday
    ? new Date()
    : firstNotedHasAny
      ? durationToSinceWhen(firstNotedY, firstNotedM, firstNotedD)
      : null;

  const lastReactionHasAny = (lastReactionY || 0) > 0 || (lastReactionM || 0) > 0 || (lastReactionD || 0) > 0;
  const derivedLastReaction = lastReactionFromToday
    ? new Date()
    : lastReactionHasAny
      ? durationToSinceWhen(lastReactionY, lastReactionM, lastReactionD)
      : null;

  // Cross-field validation: a reaction can't have happened BEFORE
  // the allergy was first noted. When the derived Last reaction sits
  // earlier than the derived First noted, clear the last-reaction
  // steppers instead of blocking Save (matches the pre-Duration
  // behaviour of native <input type="date"> auto-clearing).
  useEffect(() => {
    if (!derivedFirstNoted || !derivedLastReaction) return;
    if (derivedLastReaction < derivedFirstNoted) {
      setLastReactionY(null);
      setLastReactionM(null);
      setLastReactionD(null);
      setLastReactionFromToday(false);
    }
  }, [derivedFirstNoted, derivedLastReaction]);

  // Stepper wrappers — dropping the "From today" flag as soon as a
  // non-zero value lands (CC's rule). Wrapping every setter keeps the
  // invariant local; the auto-flip effect above re-enables it when
  // the reviewer clears every field back to 0.
  const changeFirstNotedY = (v) => { setFirstNotedY(v); if ((v || 0) > 0) setFirstNotedFromToday(false); };
  const changeFirstNotedM = (v) => { setFirstNotedM(v); if ((v || 0) > 0) setFirstNotedFromToday(false); };
  const changeFirstNotedD = (v) => { setFirstNotedD(v); if ((v || 0) > 0) setFirstNotedFromToday(false); };
  const changeLastReactionY = (v) => { setLastReactionY(v); if ((v || 0) > 0) setLastReactionFromToday(false); };
  const changeLastReactionM = (v) => { setLastReactionM(v); if ((v || 0) > 0) setLastReactionFromToday(false); };
  const changeLastReactionD = (v) => { setLastReactionD(v); if ((v || 0) > 0) setLastReactionFromToday(false); };

  const toggleFirstNotedFromToday = () => {
    if (firstNotedFromToday) {
      setFirstNotedFromToday(false);
      setFirstNotedY(null); setFirstNotedM(null); setFirstNotedD(null);
      return;
    }
    setFirstNotedFromToday(true);
    setFirstNotedY(0); setFirstNotedM(0); setFirstNotedD(0);
  };
  const toggleLastReactionFromToday = () => {
    if (lastReactionFromToday) {
      setLastReactionFromToday(false);
      setLastReactionY(null); setLastReactionM(null); setLastReactionD(null);
      return;
    }
    setLastReactionFromToday(true);
    setLastReactionY(0); setLastReactionM(0); setLastReactionD(0);
  };

  // Update has no allergen field to validate — First noted is the only
  // identity requirement, exactly as the dialog had it. Add layers the
  // allergen picker on top: both standalone and transcript now require
  // a real catalog pick (allergenCatalog shares its _id namespace with
  // recipes.ingredients post-ZY-10339, so AI-emitted names resolve
  // via the autocomplete's mount-time match the same way the medicine
  // picker does — free-text allergens no longer save).
  const canSubmit =
    !blockedBySibling &&
    hasReactionOrDescription &&
    (isUpdate
      ? !busy && !!derivedFirstNoted
      : !busy && !!name.trim() && catalogPicked && !dupError && !!derivedFirstNoted);

  const submit = () => {
    if (!canSubmit) return;
    // Transcript mode is patient-less on the sheet (the wrapper owns the
    // patient + persistence). Standalone still requires it.
    if (!isTranscript && !patientId) return;
    setFeedback(null);
    const shared = {
      reactions,
      firstNoted: toIsoUtc(derivedFirstNoted),
      lastReaction: derivedLastReaction ? toIsoUtc(derivedLastReaction) : undefined,
      description: description.trim(),
    };
    const body = isUpdate
      ? {
          // beans.UpdateRequest: identity is omitted (server-preserved) and
          // Category has no field on this board — the stored value rides
          // through unchanged.
          category: editing.category || "Other",
          ...shared,
        }
      : {
          name: name.trim(),
          category,
          catalogId,
          mCode,
          source,
          ...shared,
        };

    // Transcript mode — hand out; caller owns persistence. Skip the local
    // submitting/feedback + ALG_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    const svc = new AllergyService();
    const done = () => {
      EventService.emit(ALG_V2_REFRESH);
      // Unmounted means the rail already dropped this sheet, and closeSheet
      // clears the saving flag on the way out — so there is nothing to hand
      // back and no state to touch.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    const failed = (err) => {
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({ kind: "err", text: (err && err.message) || "Save failed — please retry" });
    };
    // Fresh version per mutation, never cached.
    svc.list(
      patientId,
      (fresh) => {
        const withVersion = { ...body, expectedVersion: (fresh && fresh.version) || 0 };
        if (isUpdate) {
          svc.update(patientId, editing.allergyId, withVersion, done, failed);
        } else {
          svc.add(patientId, withVersion, done, failed);
        }
      },
      failed
    );
  };

  const foldId = `al3-fold-${formId}`;

  const rootClass = [
    "cc3-form",
    "al3-form",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";

  return (
    <div className={rootClass}>
      {/* ZY-10356 Phase 6 Rule 1 — top-right X close only on re-opens
          (#2/#4/#6/#8). First-opens (#1/#3/#5/#7) leave the footer
          Reject as the sole dismiss affordance; gate matches CC/Med. */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={() => onClose(formId)}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* Update mode carries the allergen in the heading instead of a field
          (3296:2854); the dialog's own metrics (.al3-update) stay with the
          dialog — this is the rail, so only the title styling is reused.
          The chevron-up folds everything below this row, footer included. */}
      {isUpdate ? (
        <div className="al3-update-head">
          {/* The badge rides INSIDE the title span: .al3-update-head is a
              space-between flex row, so a bare sibling badge would be flung
              to the far edge, away from the text it numbers. */}
          <span className="al3-update-title">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Update &middot; {editing.name}
          </span>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this update" : "Collapse this update"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      ) : lockAllergenName ? (
        /* Transcript-mode re-edit — reviewer opened an already-captured
           add via ReviewSave click-to-edit. Lock the allergen and drop the
           picker so the identity can't drift between review and save. */
        <div className="al3-update-head">
          <span className="al3-update-title">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Allergy <span className="cc3-update-name">&middot; {name || "(none)"}</span>
          </span>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this allergy" : "Collapse this allergy"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      ) : (
        <div className="cc3-lbl-row">
          <label className="cc3-lbl">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Allergen<span className="cc3-req">*</span>{" "}
            <span className="cc3-lbl-hint">&middot; fuzzy search</span>
          </label>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this allergy" : "Collapse this allergy"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      )}

      <div className={`cc3-fold${isUpdate ? "" : " is-tight"}`} id={foldId}>
        <div className="cc3-fold-inner">
      {!isUpdate && !lockAllergenName && (
        /* Picker is live for standalone AND for transcript-mode manual /
           AI-proposal cards — the reviewer must be able to search for an
           allergen or refine the AI-seeded name. Only the re-edit branch
           (lockAllergenName=true, set from ReviewSave click-to-edit)
           freezes the identity.

           Post-pick renders as a bordered .alg-locked box (name + purple
           category chip + × clear) matching the Physical activity
           picker's "Badminton × Sports" pattern — the dropdown option's
           purple chip previews exactly what the picked state will look
           like. Clear returns to the searchable autocomplete without a
           form reload. */
        <div className="cc3-field">
          {catalogPicked ? (
            <div className="alg-locked">
              <span className="alg-locked-name">{name}</span>
              <span className="alg-locked-tail">
                {category && (
                  <span className="alg-locked-tag">{category}</span>
                )}
                <button
                  type="button"
                  className="alg-locked-clear"
                  onClick={() =>
                    handleAllergenPick({
                      name: "", category: "", catalogId: "", mCode: "", source: "free-text",
                    })
                  }
                  disabled={busy}
                  aria-label="Change allergen"
                  title="Change allergen"
                >
                  ×
                </button>
              </span>
            </div>
          ) : (
            <AllergenCatalogAutocomplete
              value={name}
              catalogId={catalogId}
              mCode={mCode}
              onChange={handleAllergenPick}
              disabled={busy}
              isAlreadyActive={isAllergenAlreadyActive}
            />
          )}
          {dupError && <div className="cc-feedback is-err al3-dup">{dupError}</div>}
        </div>
      )}

      <div className="cc3-field">
        <label className="cc3-lbl">
          Reaction <span className="cc3-lbl-hint">&middot; multi-select</span>
        </label>
        {/* Search box → picked chip rail → Suggested rail. Mirrors the
            Family history Conditions picker (RelativeForm.js) so the
            two "multi-select with a catalog + a quick-pick rail" fields
            behave identically: chips live BETWEEN the search and the
            Suggested rail, and a picked item drops out of the Suggested
            rail so it never renders in two places at once. */}
        <div className="al3-search-wrap" ref={searchWrapRef}>
          <input
            className="al3-search-input"
            type="text"
            value={reactionText}
            onChange={(e) => { setReactionText(e.target.value); setReactionsDropdownOpen(true); }}
            onFocus={() => setReactionsDropdownOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            placeholder="Search reactions…"
            disabled={submitting}
          />
          {/* Dropdown — every active reaction, filtered by the input's
              substring. Click adds; already-picked rows are greyed with
              an "already added" pill and no-op on click, matching the
              drug picker and Physical activity catalogs. */}
          {reactionsDropdownOpen && (
            <div className="al3-reactions-menu" role="listbox">
              {filteredReactions.length === 0 ? (
                <div className="al3-reactions-empty">
                  No matches — refine the search to pick from the list.
                </div>
              ) : (
                filteredReactions.map((r) => {
                  const nm = String(r.reaction || "").trim();
                  const already = pickedKeys.has(nm.toLowerCase());
                  return (
                    <button
                      key={r.id || nm}
                      type="button"
                      className={`al3-reactions-opt${already ? " is-already-active" : ""}`}
                      aria-disabled={already}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { if (!already) commitFromCatalog(nm); }}
                      disabled={busy}
                    >
                      <span className="al3-reactions-opt-name">{nm}</span>
                      {already && (
                        <span className="al3-reactions-opt-tag" aria-hidden="true">
                          already added
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Picked reactions — ALL selected items render as chips here.
            Removed the split-brain "non-suggested tokens sit inside the
            input; suggested toggle on the rail" model — every pick now
            lives in this rail, matching the FH Conditions UX. */}
        {reactions.length > 0 && (
          <div className="al3-chiprail">
            {reactions.map((r) => (
              <span className="al3-chip is-on" key={r.name}>
                {r.name}
                <button
                  type="button"
                  className="al3-chip-x"
                  onClick={() => setReactions((prev) => removeReaction(prev, r.name))}
                  aria-label={`Remove ${r.name}`}
                  disabled={busy}
                >
                  &#10005;
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Suggested chips = reactions where isCommon=true. Already-picked
            items are HIDDEN from this rail (they render in the chiprail
            above), so a suggested pill never doubles as its own
            "unpicked→picked" state — click = add, remove happens on the
            chiprail chip's ×. */}
        <div className="al3-suggests">
          <span className="al3-suggest-lbl">Suggested</span>
          {reactionsCatalog
            .filter((r) => r.isCommon)
            .filter((r) => {
              const label = String(r.reaction || "").trim().toLowerCase();
              return !reactions.some((x) => x.name.toLowerCase() === label);
            })
            .map((r) => {
              const label = String(r.reaction || "").trim();
              return (
                <button
                  key={r.id || label}
                  type="button"
                  className="al3-suggest"
                  onClick={() => setReactions((prev) => upsertReaction(prev, label))}
                  disabled={busy}
                >
                  <ChipPlusIcon />
                  {label}
                </button>
              );
            })}
        </div>
      </div>

      {/* Duration control — same shape as CC's "Since when": three
          steppers + a "From today" toggle + a live auto-calc line.
          First noted is REQUIRED; Last reaction OPTIONAL. The two
          cards STACK vertically (NOT the two-column `.cc3-two` layout
          the plain date inputs used) so each keeps its own full-width
          stepper row and auto-calc line without them colliding. */}
      <div className="cc3-field">
        <div className="cc3-duration-head">
          <label className="cc3-lbl">
            First noted<span className="cc3-req">*</span>
          </label>
          <label className="med3-toggle-wrap cc3-from-today">
            <input
              type="checkbox"
              checked={firstNotedFromToday}
              onChange={toggleFirstNotedFromToday}
              disabled={submitting}
            />
            <span className="med3-toggle" aria-hidden="true" />
            <span className="med3-toggle-lbl">From today</span>
          </label>
        </div>
        <div className="cc-duration" role="group" aria-label="First noted duration">
          <Stepper label="Years" value={firstNotedY} onChange={changeFirstNotedY} max={120} nullable disabled={submitting} />
          <Stepper label="Months" value={firstNotedM} onChange={changeFirstNotedM} max={11} nullable disabled={submitting} />
          <Stepper label="Days" value={firstNotedD} onChange={changeFirstNotedD} max={31} nullable disabled={submitting} />
        </div>
        <p className="cc3-autocalc">
          Auto-calc &middot; First noted:{" "}
          <strong>{derivedFirstNoted ? formatShortDate(derivedFirstNoted) : "—"}</strong>
        </p>
      </div>
      <div className="cc3-field">
        <div className="cc3-duration-head">
          <label className="cc3-lbl">
            Last reaction <span className="cc3-lbl-opt">optional</span>
          </label>
          <label className="med3-toggle-wrap cc3-from-today">
            <input
              type="checkbox"
              checked={lastReactionFromToday}
              onChange={toggleLastReactionFromToday}
              disabled={submitting}
            />
            <span className="med3-toggle" aria-hidden="true" />
            <span className="med3-toggle-lbl">From today</span>
          </label>
        </div>
        <div className="cc-duration" role="group" aria-label="Last reaction duration">
          <Stepper label="Years" value={lastReactionY} onChange={changeLastReactionY} max={120} nullable disabled={submitting} />
          <Stepper label="Months" value={lastReactionM} onChange={changeLastReactionM} max={11} nullable disabled={submitting} />
          <Stepper label="Days" value={lastReactionD} onChange={changeLastReactionD} max={31} nullable disabled={submitting} />
        </div>
        <p className="cc3-autocalc">
          Auto-calc &middot; Last reaction:{" "}
          <strong>{derivedLastReaction ? formatShortDate(derivedLastReaction) : "—"}</strong>
        </p>
      </div>

      <div className="cc3-field">
        <div className="cc3-lbl-row">
          <label className="cc3-lbl">
            Description <span className="cc3-lbl-opt">optional</span>
          </label>
          {/* Board prints a static "Max 250 words"; upstream shipped a live
              counter. Board copy in the state the board depicts (empty),
              counter once there is content — neither is lost. */}
          <span className="cc3-lbl-max">
            {description
              ? `${countWords(description)} / ${MAX_DESC_WORDS} words`
              : `Max ${MAX_DESC_WORDS} words`}
          </span>
        </div>
        <textarea
          className="fh3-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(capWords(e.target.value, descLimit.current))}
          placeholder="Add a short note..."
          disabled={submitting}
        />
        {/* Joint requirement — an allergy record with neither a reaction
            nor a description says nothing clinically useful, so we gate
            Save on the pair. Message renders as a static hint whenever
            both are empty (no error styling until Save is clicked would
            hide the rule; keeping it visible communicates the shape of
            the form upfront). */}
        {!hasReactionOrDescription && (
          <p className="al3-either-hint">
            Provide at least one of <strong>Reaction</strong> or <strong>Description</strong>.
          </p>
        )}
      </div>

      {displayError && <div className="cc-feedback is-err">{displayError}</div>}

      <div className="cc3-foot">
        {/* Stays live while saving ON PURPOSE. A write that never settles
            (the token-refresh path can swallow its callbacks) would otherwise
            pin the stack-wide save lock with no way out — every sheet's Save
            dead and this sheet undismissable until a reload. Closing mid-write
            is safe: the rail clears the lock, and both done() and failed()
            bail on !mounted, so a late response touches nothing. */}
        <button
          type="button"
          className="cc3-cancel"
          onClick={() => {
            if (isTranscript && !busy && typeof onReject === "function") {
              onReject();
              return;
            }
            onClose(formId);
          }}
        >
          {busy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
        </button>
        <button
          type="button"
          className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSubmit}
          title={blockedBySibling ? "Another allergy is saving" : undefined}
        >
          {busy
            ? (isUpdate ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
            : blockedBySibling
            ? "Waiting…"
            : isUpdate
            ? "Update"
            : isTranscript
            ? "Add"
            : "Save allergy"}
        </button>
      </div>
        </div>
      </div>
    </div>
  );
};

AllergyForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  /** null = blank add; an allergy record = updating that record. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  onReject: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the allergen (name + tag) as read-only. Add-mode transcript
   * callers set true when reopening an already-saved captured item
   * (review click-to-edit) so the reviewer can only tweak reactions /
   * dates / description from there.
   */
  lockName: PropTypes.bool,
  /** Transcript-mode: true when re-opening a staged item. Gates the X top-right. */
  isReopen: PropTypes.bool,
};

AllergyForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
  isReopen: false,
};

export default AllergyForm;
